<template>
  <div class="ep">
    <div class="ep-head">
      <span class="ep-eyebrow">Enrolment</span>
      <span class="ep-count">
        <strong>{{ selections.length }}</strong> selected
      </span>
    </div>
    <p class="ep-sub">Search people, teams, or saved cohorts. They'll be enrolled when you create the batch.</p>

    <!-- Chips render above the search so additions accrete near the click,
         not below a (now-empty) suggestion list. -->
    <div v-if="selections.length" class="ep-chips">
      <span
        v-for="s in selections"
        :key="s.kind + ':' + s.id"
        class="ep-chip"
        :class="'is-' + s.kind"
      >
        <i :class="iconForKind(s.kind)"/>
        {{ s.name }}
        <button type="button" class="ep-chip-x" @click="remove(s)" :aria-label="`Remove ${s.name}`">
          <i class="el-icon-close"/>
        </button>
      </span>
    </div>

    <div class="ep-search">
      <i class="el-icon-search"/>
      <el-input
        :value="query"
        @input="(v) => (query = v)"
        placeholder="Search names, teams, or cohorts…"
      />
    </div>

    <div v-if="suggestions.length" class="ep-suggestions">
      <div
        v-for="s in suggestions"
          :key="s.kind + ':' + s.id"
          class="ep-suggestion"
          role="button"
          tabindex="0"
          @click="add(s)"
          @keydown.enter.prevent="add(s)"
          @keydown.space.prevent="add(s)"
      >
        <span class="ep-suggestion-icon" :class="'is-' + s.kind">
          <i :class="iconForKind(s.kind)"/>
        </span>
        <div class="ep-suggestion-main">
          <div class="ep-suggestion-name">{{ s.name }}</div>
          <div class="ep-suggestion-meta">{{ s.meta }}</div>
        </div>
        <span class="ep-suggestion-add">+ Add</span>
      </div>
    </div>

    <div v-if="query.trim() && !suggestions.length" class="ep-empty ep-empty--search">{{ emptySearchMessage }}</div>
    <div v-else-if="!selections.length && !suggestions.length" class="ep-empty">No one added yet.</div>
  </div>
</template>

<script>
const SUGGESTION_LIMIT = 8
const ALL_AUDIENCE_ID = 'all_teams_and_cohorts'
const KIND_ORDER = { person: 0, team: 1, cohort: 2 }

function text(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function firstText(...values) {
  return values.map(text).find(Boolean) || ''
}

function compactJoin(values, separator = ' · ') {
  return values.map(text).filter(Boolean).join(separator)
}

function collectionValues(source) {
  if (!source || typeof source !== 'object') return []
  return Object.keys(source)
    .map((key) => {
      const value = source[key]
      if (!value || typeof value !== 'object') return null
      return {
        ...value,
        key: value.key || value.id || value.value || key,
      }
    })
    .filter(Boolean)
}

function selectionKey(item) {
  return `${item.kind}:${item.id}`
}

function pluralizePeople(count) {
  return `${count} ${count === 1 ? 'person' : 'people'}`
}

function userDisplayName(user) {
  return firstText(
    user.fullname,
    user.full_name,
    user.displayName,
    user.name,
    compactJoin([user.firstname, user.lastname], ' '),
    user.email,
    user.key
  )
}

function isInactiveUser(user) {
  const status = text(user.status || user.state || user.memberStatus).toLowerCase()
  return Boolean(
    user.archive_date ||
    user.archiveSchedule ||
    user.exitdated ||
    user.deleted ||
    user.delete === true ||
    status === 'inactive' ||
    status === 'archived' ||
    status === 'deleted'
  )
}

function userTeamIds(user) {
  const team = user.team || user.team_id || user.teamId
  if (Array.isArray(team)) return team.map(text).filter(Boolean)
  if (team && typeof team === 'object') return [team.key, team.id, team.value].map(text).filter(Boolean)
  return text(team) ? [text(team)] : []
}

function cohortUserCount(cohort) {
  if (!cohort || !cohort.users) return null
  if (Array.isArray(cohort.users)) return cohort.users.length
  if (typeof cohort.users === 'object') return Object.keys(cohort.users).length
  return null
}

function searchScore(item, query) {
  const q = query.toLowerCase()
  const name = (item.name || '').toLowerCase()
  const meta = (item.meta || '').toLowerCase()
  const search = (item.search || '').toLowerCase()

  if (name === q) return 0
  if (name.startsWith(q)) return 1
  if (name.includes(q)) return 2
  if (meta.includes(q)) return 3
  if (search.includes(q)) return 4
  return 99
}

export default {
  name: 'enrollment-picker',
  props: {
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      query: '',
      loadingDirectory: false,
      directoryLoaded: false,
      directoryError: false,
    }
  },
  computed: {
    allUsers() {
      return (this.$store && this.$store.getters['question/allUsers']) || []
    },
    teamAll() {
      return (this.$store && this.$store.getters['question/teamAll']) || []
    },
    cohortsUser() {
      return (this.$store && this.$store.getters['question/cohortsUser']) || {}
    },
    optionTo() {
      return (this.$store && this.$store.getters['question/optionTo']) || []
    },
    selections() {
      return this.value || []
    },
    activeUsers() {
      return collectionValues(this.allUsers).filter((user) => !isInactiveUser(user))
    },
    teamRows() {
      const rows = []
      const seen = new Set()
      const addTeam = (team) => {
        const id = text(team.key || team.id || team.value)
        const name = firstText(team.name, team.label)
        if (!id || id === ALL_AUDIENCE_ID || !name || seen.has(id)) return
        seen.add(id)
        const memberCount = this.activeUsers.filter((user) => userTeamIds(user).includes(id)).length
        rows.push({
          id,
          kind: 'team',
          name,
          meta: memberCount ? pluralizePeople(memberCount) : 'Team',
          search: compactJoin([name, team.description, team.parentName, 'team']),
        })
      }

      collectionValues(this.teamAll).forEach(addTeam)
      collectionValues(this.optionTo)
        .filter((option) => option.type === 'team')
        .forEach(addTeam)

      return rows
    },
    teamNameById() {
      return this.teamRows.reduce((map, team) => {
        map[team.id] = team.name
        return map
      }, {})
    },
    personRows() {
      return this.activeUsers
        .map((user) => {
          const id = firstText(user.email, user.key, user.id)
          const name = userDisplayName(user)
          if (!id || !name) return null
          const teamNames = userTeamIds(user).map((teamId) => this.teamNameById[teamId]).filter(Boolean)
          const meta = compactJoin([
            teamNames.join(', '),
            user.title || user.role || user.position || user.user_type,
            user.email,
          ])
          return {
            id,
            kind: 'person',
            name,
            meta: meta || 'Person',
            search: compactJoin([
              name,
              user.email,
              teamNames.join(' '),
              user.title,
              user.role,
              user.position,
              user.user_type,
            ]),
          }
        })
        .filter(Boolean)
    },
    cohortRows() {
      const rows = []
      const seen = new Set()
      const cohortsById = collectionValues(this.cohortsUser).reduce((map, cohort) => {
        if (cohort.key) map[cohort.key] = cohort
        return map
      }, {})

      const addCohort = (idValue, cohortValue = {}) => {
        const id = text(idValue || cohortValue.key || cohortValue.id || cohortValue.value)
        const name = firstText(cohortValue.name, cohortValue.label)
        if (!id || id === ALL_AUDIENCE_ID || !name || seen.has(id)) return
        seen.add(id)
        const count = cohortUserCount(cohortValue)
        rows.push({
          id,
          kind: 'cohort',
          name,
          meta: count === null ? 'Saved cohort' : `Saved cohort · ${pluralizePeople(count)}`,
          search: compactJoin([name, cohortValue.description, 'saved cohort cohort']),
        })
      }

      Object.keys(cohortsById).forEach((id) => addCohort(id, cohortsById[id]))
      collectionValues(this.optionTo)
        .filter((option) => option.type === 'cohorts' || option.type === 'cohort')
        .forEach((option) => {
          const id = option.value || option.key
          addCohort(id, {
            ...cohortsById[id],
            ...option,
            name: option.label || option.name,
          })
        })

      return rows
    },
    directory() {
      return [
        ...this.personRows,
        ...this.teamRows,
        ...this.cohortRows,
      ]
    },
    emptySearchMessage() {
      if (this.loadingDirectory && !this.directory.length) return 'Loading people, teams, and cohorts…'
      if (this.directoryError) return 'Unable to load people, teams, and cohorts. Try again.'
      return 'No matching people, teams, or cohorts.'
    },
    suggestions() {
      const q = this.query.trim().toLowerCase()
      if (!q) return []
      const taken = new Set(this.selections.map(selectionKey))
      return this.directory
        .filter((s) => !taken.has(selectionKey(s)))
        .map((s) => ({ ...s, _score: searchScore(s, q) }))
        .filter((s) => s._score < 99)
        .sort((a, b) => (
          a._score - b._score ||
          KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
          a.name.localeCompare(b.name)
        ))
        .slice(0, SUGGESTION_LIMIT)
        .map(({ _score, ...s }) => s)
    },
  },
  mounted() {
    this.loadDirectory()
  },
  methods: {
    async loadDirectory() {
      if (!this.$store || !this.$store.dispatch) return
      this.loadingDirectory = true
      this.directoryError = false
      try {
        await Promise.all([
          this.$store.dispatch('question/getUser', {}),
          this.$store.dispatch('question/getChildren', {}),
        ])
        await this.$store.dispatch('question/getTeamAndCohort', {})
        this.directoryLoaded = true
      } catch (e) {
        this.directoryError = true
      } finally {
        this.loadingDirectory = false
      }
    },
    iconForKind(kind) {
      switch (kind) {
        case 'person': return 'el-icon-user'
        case 'team':   return 'el-icon-office-building'
        case 'cohort': return 'el-icon-data-board'
        default:       return 'el-icon-circle-plus-outline'
      }
    },
    add(s) {
      this.$emit('input', [
        ...this.selections,
        {
          id: s.id,
          kind: s.kind,
          name: s.name,
          meta: s.meta,
        },
      ])
      this.query = ''
    },
    remove(s) {
      const key = s.kind + ':' + s.id
      this.$emit('input', this.selections.filter((x) => (x.kind + ':' + x.id) !== key))
    },
  },
}
</script>

<style scoped>
.ep { display: flex; flex-direction: column; gap: 10px; }

.ep-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.ep-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.ep-count {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.ep-count strong {
  color: #6c30cc;
  font-weight: 800;
  font-size: 13px;
  margin-right: 2px;
}
.ep-sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: -4px 0 4px;
}

.ep-search {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 10px;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  padding: 6px 12px;
}
.ep-search i { color: #c0c4cc; }
.ep-search >>> .el-input__inner {
  border: none;
  padding: 0;
  height: 32px;
  background: transparent;
}

.ep-suggestions {
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 10px;
  overflow: hidden;
}
.ep-suggestion {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background .15s ease;
}
.ep-suggestion:focus-visible {
  outline: 3px solid rgba(108, 48, 204, 0.24);
  outline-offset: -3px;
}
.ep-suggestion:last-child { border-bottom: none; }
.ep-suggestion:hover { background: rgba(108, 48, 204, 0.04); }
.ep-suggestion-icon {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: #f6f7f9;
  color: #6b7280;
}
.ep-suggestion-icon.is-team   { background: rgba(50, 194, 213, 0.10); color: #1c8b9a; }
.ep-suggestion-icon.is-cohort { background: rgba(108, 48, 204, 0.10); color: #6c30cc; }
.ep-suggestion-name {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
}
.ep-suggestion-meta {
  font-size: 10px;
  color: #6b7280;
  margin-top: 1px;
}
.ep-suggestion-add {
  font-size: 10px;
  font-weight: 700;
  color: #6c30cc;
}

.ep-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ep-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e4ea;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
}
.ep-chip i { color: #6b7280; font-size: 12px; }
.ep-chip.is-team i   { color: #1c8b9a; }
.ep-chip.is-cohort i { color: #6c30cc; }
.ep-chip-x {
  appearance: none;
  background: transparent;
  border: none;
  color: #c0c4cc;
  line-height: 1;
  padding: 0 0 0 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.ep-chip-x i { font-size: 13px; }
.ep-chip-x:hover { color: #6c30cc; }

.ep-empty {
  padding: 10px 12px;
  text-align: center;
  font-size: 12px;
  color: #8a8a8a;
  font-style: italic;
  border: 1px dashed #e2e4ea;
  border-radius: 8px;
}
</style>
